/**
 * ============================================================================
 *  SITE_EDITION — 前端侧的部署级版本开关
 * ============================================================================
 *
 * 由 `REACT_APP_SITE_EDITION` 在**构建期**注入（deploy/clients/*.conf 里配）。
 * 不设 = 'commercial'，即商业版行为，和这个开关引入前完全一致。
 *
 * 版本标识：
 *   - commercial: Cineart Canvas 商业版（主站）
 *   - edu: Cineart Canvas 教育版
 *   - huanshe-edu: 环艺学姐教育版
 *   - huanshe-commercial: 环艺学姐商业版
 *
 * ⚠️ 为什么这里写成 `process.env.X === 'edu'` 而不是先取出来再比较：
 *    CRA 会把 `process.env.REACT_APP_SITE_EDITION` **原地替换成字符串字面量**，
 *    所以这一行在商业版构建里就是 `"commercial" === "edu"` → terser 直接折成 false，
 *    引用它的 `{IS_EDU && <付费入口/>}` 整块被当死代码删掉。
 *    若写成 `(process.env.X || 'commercial').toLowerCase() === 'edu'`，
 *    折叠就要指望 terser 对 `.toLowerCase()` 求值，没保证 —— 别改成那样。
 *
 * ⚠️ 但**只靠这个开关不够**：`import RechargeModal from './RechargeModal'` 是静态导入，
 *    就算调用点被删了，模块内容（「充值」「虎皮椒」「¥」）照样进 bundle。
 *    所以教育版构建时还会把 RechargeModal.js 换成 RechargeModal.edu-stub.js，
 *    见 deploy/deploy-client.sh 的 override_edition()。
 */

const SITE_EDITION = process.env.REACT_APP_SITE_EDITION || 'commercial';

/**
 * 🎓 是否教育版 —— **编译期常量，必须用纯字面量比较**。
 *
 * ⚠️⚠️ 为什么不能写 `SITE_EDITION.endsWith('-edu')`：
 *    CRA 把 process.env.REACT_APP_SITE_EDITION 原地替换成字符串后，
 *    代码变成 `"huanshe-edu".endsWith("-edu")` —— terser **不会去执行
 *    endsWith 求值**（它只折叠字面量之间的比较），所以这个表达式在产物里
 *    原样保留，整个表达式被当成运行时值。
 *
 *    后果：所有靠它守卫的常量（如 PAYMENT_DEFAULT_CONFIG）虽然运行时走对分支，
 *    但**死分支里的字面量仍进 bundle** —— 教育版产物里照样留着虎皮椒接口地址、
 *    「请使用微信扫码支付」、整张价目表。实测踩过。
 *
 * ✅ 正确做法：用一个纯 true/false 的 env 变量（REACT_APP_IS_EDU），
 *    代码写成 `process.env.REACT_APP_IS_EDU === 'true'` —— 这是两个字符串字面量
 *    的比较，terser 100% 折叠，整个死分支连同里面的字符串一起删干净。
 *
 *    部署脚本（deploy/deploy-client.sh 的 override_env_prod）负责按 SITE_EDITION
 *    自动注入这个变量，代码里不需要自己推导。
 */
export const IS_EDU = process.env.REACT_APP_IS_EDU === 'true';

/** 商业版 = 非教育版。写成显式常量，读代码时不用反着想。 */
export const IS_COMMERCIAL = !IS_EDU;

/**
 * 是否环艺学姐站点。
 * ⚠️ 同样用字面量比较（REACT_APP_IS_HUANSHE），不用 startsWith() ——
 *    原因同上：方法调用 terser 不折叠。
 */
export const IS_HUANSHE = process.env.REACT_APP_IS_HUANSHE === 'true';

/**
 * ✅ 是否启用支付（编译期常量，纯字面量比较，terser 可折叠）。
 *
 * edu / *-edu 一律无支付。判定只认 REACT_APP_IS_EDU，不再依赖
 * REACT_APP_ENABLE_PAYMENT（conf 里常常没配，漏配就等于没开）。
 */
export const HAS_PAYMENT = process.env.REACT_APP_IS_EDU !== 'true';

/** 获取站点版本标识 */
export const SITE_EDITION_NAME = SITE_EDITION;

/**
 * 后台「管理员给用户加积分」这个动作的措辞。
 *
 * 它本身跟钱无关（超管无中生有 / 普管从自己余额转），但**「充值」两个字**
 * 在合规扫描眼里就是收费信号。教育版整站不能出现付费暗示，为了不让人工复核
 * 卡在一个假阳性上，edu 下统一改口叫「发放」（也和阶段 4 的算力券对得上）。
 *
 * ⚠️ 必须用纯字面量比较（process.env.REACT_APP_IS_EDU === 'true'）：
 *    写成 `.endsWith('-edu')` 这种方法调用，terser 不会折叠，
 *    「充值」两个字会原样留在教育版产物里（实测过）。
 */
export const CHARGE_WORD = process.env.REACT_APP_IS_EDU === 'true' ? '发放' : '充值';

/** 站点显示名称（运行时从后端 API 获取，这里只是兜底） */
export const SITE_NAME_FALLBACK = IS_HUANSHE ? '环艺学姐' : 'Cineart Canvas';
